import { createContext, useState, useMemo, useContext, ReactNode, Dispatch, SetStateAction, useEffect } from 'react';

export type HostContextType<T> =
	| {
			hostProps: T;
			setHostProps: Dispatch<SetStateAction<T>>;
	  }
	| undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const HostContext = createContext<HostContextType<any>>(undefined);
HostContext.displayName = 'HostContext';

function defaultResolver<T>(values: T): T {
	return values;
}

export function HostContextProvider<T>({
	children,
	values,
	resolver = defaultResolver,
}: {
	children: ReactNode;
	values: T;
	resolver?: (values: T) => T;
}) {
	const [hostProps, setHostProps] = useState(() => resolver(values));

	useEffect(() => {
		setHostProps(resolver(values));
	}, [values]);

	const ctxValue = useMemo(() => ({ hostProps, setHostProps }), [hostProps]);

	return <HostContext.Provider value={ctxValue}>{children}</HostContext.Provider>;
}

export function useHostAPI<T>() {
	return useContext(HostContext) as NonNullable<HostContextType<T>>;
}
